import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { notifyApiError } from '@/lib/toast';
import { useCustomerPortalMutation } from '@/features/billing/queries';
import { isNoBillingPortal } from '@/features/billing/plan-helpers';

type Props = {
  /** Visible button copy. Defaults to the generic "Manage billing". */
  label?: string;
  /**
   * Accessible name, when the visible label needs disambiguating — e.g. one
   * portal button per plan row, where three identical "Manage billing" names
   * would be ambiguous to a screen-reader user.
   */
  ariaLabel?: string;
  /** Button size / variant, matching the surrounding surface. */
  size?: 'sm' | 'default';
  variant?: 'secondary' | 'outline' | 'link' | 'ghost';
  /** Optional leading glyph, for a surface whose sibling actions carry one. */
  icon?: React.ReactNode;
  /** Applied to both the button and the no-portal explanation. */
  className?: string;
  /** Applied to the button only — e.g. `ALERT_BANNER_ACTION_CLASS` in a banner. */
  buttonClassName?: string;
  /** Applied to the no-portal explanation only, which replaces the button. */
  fallbackClassName?: string;
  style?: React.CSSProperties;
};

/**
 * The single actionable entry point to the Paddle customer portal.
 *
 * Every path the app deliberately does NOT handle in-app — downgrades, a
 * PAST_DUE payment method, a paused/canceled subscription, an upgrade whose
 * webhook never landed — routes here. Each of those surfaces previously rendered
 * the *words* "manage in the billing portal" with no control behind them, so the
 * user hit a dead end; this component exists so that copy can never again ship
 * without the action attached.
 *
 * Error handling is split by whether retrying could ever work:
 *   - `040501 NoActiveSubscription` → **no portal exists** for this org (it has
 *     never billed). Permanent, so the button is replaced by an explanation
 *     rather than left in place to fail again. Reachable in normal use: a failed
 *     subscription read also routes to `portal` mode.
 *   - anything else (`040502`, network, 5xx) → transient. Toast it and leave the
 *     button clickable so the user can simply try again.
 *
 * The mutation is `silentError` precisely so those two cases can diverge; the
 * navigation itself lives in `useCustomerPortalMutation`.
 */
export function BillingPortalButton({
  label = 'Manage billing',
  ariaLabel,
  size = 'sm',
  variant = 'secondary',
  icon,
  className,
  buttonClassName,
  fallbackClassName,
  style,
}: Props) {
  const portal = useCustomerPortalMutation();

  // Permanent: this org has no portal to send anyone to. Explain, don't offer a
  // button whose only possible outcome is the same error.
  if (isNoBillingPortal(portal.error)) {
    return (
      <span
        role="status"
        className={cn('text-xs text-muted-foreground', className, fallbackClassName)}
        style={style}
      >
        No billing portal yet. This organization has no billing history.
      </span>
    );
  }

  return (
    <Button
      size={size}
      variant={variant}
      className={cn(className, buttonClassName)}
      style={style}
      disabled={portal.isPending}
      aria-label={ariaLabel}
      onClick={() =>
        portal.mutate(undefined, {
          onError: (err) => {
            // The no-portal case renders inline above; everything else is
            // retryable and gets the standard toast.
            if (!isNoBillingPortal(err)) {
              notifyApiError(err, "Couldn't open the billing portal");
            }
          },
        })
      }
    >
      {icon}
      {portal.isPending ? 'Opening…' : label}
    </Button>
  );
}
